'use client'

import { useRef } from 'react'
import { Text, Note, Spacer, Chip, Icon } from '@vinted/web-ui'
import { ArrowRight12 } from '@vinted/monochrome-icons'

import { InView } from 'react-intersection-observer'

import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'

import {
  selectSuggestedMessageEvent,
  viewSuggestedMessageEvent,
} from '@marketplace-web/messaging/conversation-messaging-data'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'

import usePostReply from '../../hooks/usePostReply/usePostReply'

const SuggestedMessages = () => {
  const { track } = useTracking()
  const translate = useTranslate('conversation.suggested_messages')
  const { postReplyRequest } = usePostReply()

  const { conversation } = useConversationContext()
  const suggestedMessages = conversation?.suggestedMessages

  const seenSuggestedMessagesIds = useRef<Set<number>>(new Set())

  const handleMessageClick = (id: number, text: string, position: number) => () => {
    track(
      selectSuggestedMessageEvent({
        id: id.toString(),
        position,
        transactionId: conversation?.transaction?.id,
        itemIds: conversation?.transaction?.order.itemIds?.toString(),
      }),
    )

    postReplyRequest({ value: text })
  }

  const handleSuggestedMessageVisibility = (id: number, position: number) => (inView: boolean) => {
    if (!inView) return
    if (seenSuggestedMessagesIds.current.has(id)) return

    seenSuggestedMessagesIds.current.add(id)

    track(
      viewSuggestedMessageEvent({
        id: id.toString(),
        position,
        transactionId: conversation?.transaction?.id,
        itemIds: conversation?.transaction?.order.itemIds.toString(),
      }),
    )
  }

  const renderSuggestedMessagesTitle = () => {
    return (
      <Note
        text={
          <Text
            as="h4"
            html
            text={translate('note')}
            type={Text.Type.Caption}
            alignment={Text.Alignment.Center}
          />
        }
        alignment={Note.Alignment.Center}
        styling={Note.Styling.Wide}
        inverse
      />
    )
  }

  if (!suggestedMessages?.length) return null

  return (
    <>
      {renderSuggestedMessagesTitle()}
      <SeparatedList separator={<Spacer />}>
        {suggestedMessages.map(({ id, text }, index) => (
          <InView
            className="u-flexbox u-justify-content-flex-end conversation-message-container"
            onChange={handleSuggestedMessageVisibility(id, index + 1)}
            key={id}
          >
            <Chip
              radius={Chip.Radius.Round}
              text={text}
              textType={Text.Type.Subtitle}
              suffix={<Icon name={ArrowRight12} />}
              onClick={handleMessageClick(id, text, index + 1)}
            />
          </InView>
        ))}
      </SeparatedList>
    </>
  )
}

export default SuggestedMessages
